// genericObjectModalStore.ts
import { create } from "zustand";

export type FieldSpec<T extends Record<string, any>> =
  | string
  | {
    key: string;
    label?: string;
    formatter?: (value: any, full: T) => React.ReactNode;
    hideIfEmpty?: boolean;
  };

type Status = "closed" | "open";

export type GenericModalState<T extends Record<string, any> = Record<string, any>> = {
  status: Status;
  title: string;
  data: T | null;
  fields: FieldSpec<T>[];
  emptyFallback: string;

  openModal: (args: {
    data: T;
    fields: FieldSpec<T>[];
    title?: string;
    emptyFallback?: string;
  }) => void;

  closeModal: () => void;
  reset: () => void;
  setData: (data: T | null) => void;
  // 🔹 Loading para HTTP
  isLoading: boolean;
  loadingRequestId: number | null;

  openModalAsync: (args: {
    fetcher: () => Promise<T>;
    fields: FieldSpec<T>[];
    title?: string;
    emptyFallback?: string;
  }) => Promise<void>;

  setLoading: (v: boolean) => void;
};

const initialState = {
  status: "closed" as Status,
  title: "Detalles",
  data: null,
  fields: [] as FieldSpec<any>[],
  emptyFallback: "—",
  isLoading: false,
  loadingRequestId: null as number | null,
};

export const useGenericObjectModalStore = create<GenericModalState>((set, get) => ({
  ...initialState,

  openModalAsync: async ({ fetcher, fields, title, emptyFallback }) => {
    const reqId = Date.now();
    set({
      status: "open",
      data: null,
      fields: fields as any,
      title: title ?? initialState.title,
      emptyFallback: emptyFallback ?? initialState.emptyFallback,
      isLoading: true,
      loadingRequestId: reqId,
    });

    try {
      const data = await fetcher();
      // Evita pisar con respuestas viejas
      if (get().loadingRequestId !== reqId) return;
      set({ data });
    } catch (e) {
      console.error("error:", e);
      if (get().loadingRequestId !== reqId) return;
      set({ data: null }); // o conserva el anterior si quieres
    } finally {
      if (get().loadingRequestId === reqId) {
        set({ isLoading: false, loadingRequestId: null });
      }
    }
  },

  setLoading: (v) => set({ isLoading: v }),

  openModal: ({ data, fields, title, emptyFallback }) =>
    set(() => ({
      status: "open",
      data,
      fields: fields as any,
      title: title ?? initialState.title,
      emptyFallback: emptyFallback ?? initialState.emptyFallback,
    })),

  closeModal: () => set({ status: "closed" }),
  reset: () => set({ ...initialState }),
  setData: (data) => set({ data }),
}));

// Helpers para usar fuera de componentes (sin hooks)

export function openGenericObjectModal<T extends Record<string, any>>(args: {
  data: T;
  fields: FieldSpec<T>[];
  title?: string;
  emptyFallback?: string;
}) {
  useGenericObjectModalStore.getState().openModal(args as any);
}

export function closeGenericObjectModal() {
  useGenericObjectModalStore.getState().closeModal();
}

export function openGenericObjectModalAsync<T extends Record<string, any>>(args: {
  fetcher: () => Promise<T>;
  fields: FieldSpec<T>[];
  title?: string;
  emptyFallback?: string;
  twoColumns?: boolean;
}) {
  return useGenericObjectModalStore.getState().openModalAsync(args as any);
}

export function setGenericObjectModalLoading(v: boolean) {
  useGenericObjectModalStore.getState().setLoading(v);
}